import React from 'react';
import {CourseLearnersAnalyticsItem} from '../../../../../data/models/Gradebook/CourseAnalyticsModels';
import {analyticsText} from '../../../analyticsText';
import {getFirstCapitals} from '../../../../../utils/getFirstCapitals';
import Tooltip from '../../../../../uiToolkit/Tooltip';
import {truncateNumberTo2Decimals} from '../../../../../utils/getTimeTextFromSeconds';
import {formatDateTime} from '../../../../../utils/notificationTimeTextConvertor';

interface Props {
  learners: CourseLearnersAnalyticsItem[];
  passingScore: number;
  onSelectLearner: (learnerId: string) => void;
  isGradable: boolean;
  onChangeSort: (field: string, asc: boolean) => void;
  sortField: string;
  asc: boolean;
}

const DONUT_INNER_R = 13.512195121951223;
const DONUT_OUTER_R = 20.268292682926834;
const DONUT_CX = 20;
const DONUT_CY = 20;

const polar = (r: number, deg: number) => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [DONUT_CX + r * Math.cos(rad), DONUT_CY + r * Math.sin(rad)] as const;
};

const slicePath = (startDeg: number, angleDeg: number) => {
  const rawEnd = startDeg + angleDeg;
  const endDeg = rawEnd >= 360 ? 359.99 : rawEnd;
  const [x1, y1] = polar(DONUT_INNER_R, startDeg);
  const [x2, y2] = polar(DONUT_INNER_R, endDeg);
  const [xo1, yo1] = polar(DONUT_OUTER_R, endDeg);
  const [xo2, yo2] = polar(DONUT_OUTER_R, startDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${DONUT_INNER_R} ${DONUT_INNER_R} 0 ${largeArc} 1 ${x2} ${y2} L ${xo1} ${yo1} A ${DONUT_OUTER_R} ${DONUT_OUTER_R} 0 ${largeArc} 0 ${xo2} ${yo2} L ${x1} ${y1} Z`;
};

const ProgressDonut = React.memo(
  ({status, progress}: {status: number; progress: number}) => {
    const progressColor =
      status === 0 ? '#53B483' : status === 1 ? '#3B82F6' : '#E5E7EA';
    const grayAngle = (100 - progress) * 3.6;
    const colorAngle = progress * 3.6;
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="43"
        aria-hidden="true">
        <path
          d={slicePath(0, grayAngle)}
          fill="#E5E7EA"
          stroke="#ffffff"
          strokeWidth="2"
        />
        <path
          d={slicePath(grayAngle, colorAngle)}
          fill={progressColor}
          stroke="#ffffff"
          strokeWidth="2"
        />
      </svg>
    );
  },
);

const LearnersAnalyticsTable = React.memo(
  ({
    learners,
    passingScore,
    onSelectLearner,
    isGradable,
    onChangeSort,
    sortField,
    asc,
  }: Props) => {
    const getSortingArrow = (field: string) => {
      if (sortField === field) {
        if (asc) {
          return <div className="arrow-down"></div>;
        } else {
          return <div className="arrow-up"></div>;
        }
      }
      return <div />;
    };

    return (
      <div className="CourseLearnersAnalyticsTableContainer">
        <table className="CourseLearnersAnalyticsTable">
          <thead>
            <tr>
              <th className="LearnerColumn">
                <div
                  className="SortArrowContainer"
                  style={{justifyContent: 'flex-start'}}
                  onClick={() => onChangeSort('Name', !asc)}>
                  {analyticsText.learner} {getSortingArrow('Name')}
                </div>
              </th>
              <th>
                <div
                  className="SortArrowContainer"
                  onClick={() => onChangeSort('Due date', !asc)}>
                  {analyticsText.dueDate}
                  {getSortingArrow('Due date')}
                </div>
              </th>
              <th>
                <div
                  className="SortArrowContainer"
                  onClick={() => onChangeSort('Status', !asc)}>
                  {analyticsText.status}
                  {getSortingArrow('Status')}
                </div>
              </th>
              {isGradable && (
                <th>
                  <div
                    className="SortArrowContainer"
                    onClick={() => onChangeSort('AvgScore', !asc)}>
                    {analyticsText.avgScore} {getSortingArrow('AvgScore')}
                  </div>
                </th>
              )}
              <th>
                <div
                  className="SortArrowContainer"
                  onClick={() => onChangeSort('Progress', !asc)}>
                  {analyticsText.progress} {getSortingArrow('Progress')}
                </div>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {learners.map(learner => (
              <tr key={learner.id}>
                <td className="LearnerColumn">
                  <div className="LearnerNameContainer">
                    {learner.avatarUrl ? (
                      <img src={learner.avatarUrl} />
                    ) : (
                      <div className="AvatarPlaceholder">
                        {getFirstCapitals(learner.name)}
                      </div>
                    )}
                    <Tooltip tooltipText={learner.name}>
                      <span>{learner.name}</span>
                    </Tooltip>
                  </div>
                </td>
                <td>
                  {learner.dueDate ? (
                    <span>{formatDateTime(learner.dueDate)}</span>
                  ) : (
                    <span>{analyticsText.noDueDate}</span>
                  )}
                </td>
                <td>
                  {learner.status === 0 ? (
                    <span className="StatusCompleted">
                      {analyticsText.completed}
                    </span>
                  ) : learner.status === 1 ? (
                    <span className="StatusProgress">
                      {analyticsText.inProgress}
                    </span>
                  ) : (
                    <span className="StatusNotStarted">
                      {analyticsText.notStarted}
                    </span>
                  )}
                </td>
                {isGradable && (
                  <td
                    className={
                      learner.avgScore == null
                        ? 'ScorePending'
                        : (learner.avgScore || 0) >= passingScore
                        ? 'ScoreSuccess'
                        : 'ScoreFail'
                    }>
                    {learner.avgScore != null
                      ? truncateNumberTo2Decimals(learner.avgScore) + '%'
                      : 'Pending'}
                  </td>
                )}
                <td>
                  <div className="ProgressChartContainer">
                    <ProgressDonut
                      status={learner.status}
                      progress={learner.progress}
                    />
                    <span className="Amount">{learner.progress}%</span>
                  </div>
                </td>
                <td onClick={() => onSelectLearner(learner.id)}>
                  <span className="icon-eye"></span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  },
);

export default LearnersAnalyticsTable;
